// src/cockpit4/components/WebsiteStatsPanel.tsx
// 官网访问统计看板 → GEO流量分析中心（v6.38）
//   数据源：GET /stats?key=xeaos2026&days=14
//   板块：
//     1) 总览     —— 普通流量 vs GEO 流量、关键指标
//     2) 来源溯源 —— 分平台流量（豆包/Kimi/DeepSeek…）、引流问题词 TOP
//     3) 行为追踪 —— 平均停留、跳出率、页面访问、下载/工具/官博浏览次数
//     4) 用户画像 —— IP 地区、设备、新/回访、访问时段
//     5) 意向打分 —— 基于行为自动打分，生成匿名线索（DSO）
//     6) 线索闭环 —— GEO 访客 → 留资线索转化追踪
//     7) 归因分析 —— 反向找出哪些 GEO 素材引流效果最好
//     8) 数据回流 —— 自动生成 GEO 素材优化建议，反哺上游知识库

import { useEffect, useCallback, useState } from "react";
import {
  Globe,
  Users,
  RefreshCw,
  Loader2,
  TrendingUp,
  MapPin,
  ExternalLink,
  FileText,
  Clock,
  Smartphone,
  Monitor,
  Tablet,
  Bot,
  UserCircle2,
  Radio,
  Activity,
  Fingerprint,
  Gauge,
  Target,
  GitMerge,
  DatabaseBackup,
} from "lucide-react";

const STATS_KEY = "xeaos2026";

interface DayStat { date: string; visits: number; uniqueIps: number }
interface TopItem { region?: string; page?: string; referrer?: string; count: number }
interface Visitor {
  uid: string; ip: string; region: string; device: string;
  visits: number; lastTs: number; pageCount: number;
  intentScore?: number;
}
interface DeviceItem { device: string; count: number }
interface RecentVisit {
  ts: number; ip: string; region: string; page: string;
  title: string; referrer: string; uid: string; device: string;
}
interface SourceItem { source: string; count: number }
interface PlatformItem { platform: string; count: number }
interface KeywordItem { keyword: string; count: number }
interface HourItem { hour: number; count: number }
interface TopicHit { topic: string; count: number }

interface StatsData {
  totalVisits: number;
  todayVisits: number;
  todayUniqueIps: number;
  totalVisitors: number;
  days: DayStat[];
  topPages: TopItem[];
  topReferrers: TopItem[];
  topRegions: TopItem[];
  topTodayRegions: TopItem[];
  deviceBreakdown: DeviceItem[];
  visitors: Visitor[];
  pageVisitors: Record<string, Visitor[]>;
  recent: RecentVisit[];
  geoSourceBreakdown: SourceItem[];
  geoPlatformBreakdown: PlatformItem[];
  geoKeywordTop: KeywordItem[];
  geoTotal: number;
  geoTotalVisitors: number;
  avgDuration: number;
  bounceRate: number;
  downloadCount: number;
  toolUseCount: number;
  hourBreakdown: HourItem[];
  newVisitors: number;
  returningVisitors: number;
}

const DEVICE_META: Record<string, { icon: typeof Smartphone; color: string; label: string }> = {
  手机: { icon: Smartphone, color: "#22D3EE", label: "手机" },
  电脑: { icon: Monitor, color: "#A78BFA", label: "电脑" },
  平板: { icon: Tablet, color: "#34D399", label: "平板" },
  爬虫: { icon: Bot, color: "#F87171", label: "爬虫" },
  未知: { icon: Monitor, color: "#9CA3AF", label: "未知" },
  其他: { icon: Monitor, color: "#9CA3AF", label: "其他" },
};

// 平台配色
const PLATFORM_COLOR: Record<string, string> = {
  豆包: "#22D3EE", Kimi: "#A78BFA", DeepSeek: "#60A5FA", 文心一言: "#34D399",
  通义千问: "#FBBF24", 元宝: "#F472B6", ChatGPT: "#4ADE80", Google: "#FB923C", 百度: "#F87171",
};

function avatarColor(seed: string): string {
  const colors = ["#22D3EE", "#A78BFA", "#34D399", "#FBBF24", "#F472B6", "#60A5FA", "#FB923C", "#4ADE80"];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return colors[h % colors.length];
}

// 访客昵称：地区+设备，或 uid 末 4 位
function visitorLabel(v: Visitor): string {
  const dev = DEVICE_META[v.device]?.label || "";
  if (v.region && v.region !== "未知") return `${v.region} · ${dev}`;
  return `访客 ${v.uid.slice(-4) || v.ip.slice(-4)}` + (dev ? ` · ${dev}` : "");
}

// 意向等级
function intentLevel(score: number): { label: string; color: string } {
  if (score >= 80) return { label: "A 高意向", color: "#34D399" };
  if (score >= 60) return { label: "B 中高", color: "#A3E635" };
  if (score >= 40) return { label: "C 中等", color: "#FBBF24" };
  if (score >= 20) return { label: "D 低", color: "#FB923C" };
  return { label: "E 观望", color: "#9CA3AF" };
}

type TabId = "overview" | "source" | "behavior" | "profile" | "intent" | "lead" | "attribution" | "reflux";

export default function WebsiteStatsPanel() {
  const [data, setData] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState<TabId>("overview");
  const [refluxBusy, setRefluxBusy] = useState(false);
  const [refluxMsg, setRefluxMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [topicHits, setTopicHits] = useState<TopicHit[]>([]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    setError("");
    try {
      const r = await fetch(`/stats?key=${STATS_KEY}&days=14`).catch(() => null);
      if (!r || !r.ok) {
        setError("官网统计接口不可用（/stats 未响应或口令失效）");
        setData(null);
        return;
      }
      const d = await r.json();
      if (!d.success) {
        setError(`官网统计接口报错：${d.error || "未知"}`);
        setData(null);
        return;
      }
      setData({
        totalVisits: d.totalVisits ?? 0,
        todayVisits: d.todayVisits ?? 0,
        todayUniqueIps: d.todayUniqueIps ?? 0,
        totalVisitors: d.totalVisitors ?? 0,
        days: Array.isArray(d.days) ? d.days : [],
        topPages: Array.isArray(d.topPages) ? d.topPages : [],
        topReferrers: Array.isArray(d.topReferrers) ? d.topReferrers : [],
        topRegions: Array.isArray(d.topRegions) ? d.topRegions : [],
        topTodayRegions: Array.isArray(d.topTodayRegions) ? d.topTodayRegions : [],
        deviceBreakdown: Array.isArray(d.deviceBreakdown) ? d.deviceBreakdown : [],
        visitors: Array.isArray(d.visitors) ? d.visitors : [],
        pageVisitors: d.pageVisitors && typeof d.pageVisitors === "object" ? d.pageVisitors : {},
        recent: Array.isArray(d.recent) ? d.recent : [],
        geoSourceBreakdown: Array.isArray(d.geoSourceBreakdown) ? d.geoSourceBreakdown : [],
        geoPlatformBreakdown: Array.isArray(d.geoPlatformBreakdown) ? d.geoPlatformBreakdown : [],
        geoKeywordTop: Array.isArray(d.geoKeywordTop) ? d.geoKeywordTop : [],
        geoTotal: d.geoTotal ?? 0,
        geoTotalVisitors: d.geoTotalVisitors ?? 0,
        avgDuration: d.avgDuration ?? 0,
        bounceRate: d.bounceRate ?? 0,
        downloadCount: d.downloadCount ?? 0,
        toolUseCount: d.toolUseCount ?? 0,
        hourBreakdown: Array.isArray(d.hourBreakdown) ? d.hourBreakdown : [],
        newVisitors: d.newVisitors ?? 0,
        returningVisitors: d.returningVisitors ?? 0,
      });
    } catch {
      setError("官网统计加载失败");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // 数据回流：分析访客关键词命中哪些待补充 topic
  const runReflux = useCallback(async (action: "analyze" | "generate") => {
    setRefluxBusy(true);
    setRefluxMsg(null);
    try {
      const r = await fetch("/geo-material-suggestions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      }).catch(() => null);
      const d = r ? await r.json().catch(() => null) : null;
      if (!d || !d.success) {
        setRefluxMsg({ ok: false, text: d?.error || "回流接口不可用" });
        return;
      }
      if (action === "analyze") {
        setTopicHits(Array.isArray(d.topicHits) ? d.topicHits : []);
        setRefluxMsg({ ok: true, text: "分析完成" });
      } else {
        setRefluxMsg({ ok: true, text: d.msg || "已生成素材建议" });
      }
    } catch {
      setRefluxMsg({ ok: false, text: "回流请求失败" });
    } finally {
      setRefluxBusy(false);
    }
  }, []);

  const maxRegion = Math.max(1, ...(data?.topRegions ?? []).map((r) => r.count));
  const maxPlatform = Math.max(1, ...(data?.geoPlatformBreakdown ?? []).map((p) => p.count));
  const maxKeyword = Math.max(1, ...(data?.geoKeywordTop ?? []).map((k) => k.count));
  const maxHour = Math.max(1, ...(data?.hourBreakdown ?? []).map((h) => h.count));

  const fmtTime = (ts: number) => {
    const d = new Date(ts);
    return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const fmtDur = (ms: number) => {
    if (!ms) return "—";
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m${s % 60}s`;
  };

  const Avatar = ({ v, size = 22 }: { v: { uid: string; ip: string; device: string; region: string }; size?: number }) => {
    const seed = v.uid || v.ip;
    const color = avatarColor(seed);
    const DevIcon = DEVICE_META[v.device]?.icon || UserCircle2;
    const initial = (v.region || "?").slice(0, 1);
    return (
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        <div className="rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ width: size, height: size, background: color }}>
          {initial}
        </div>
        <DevIcon size={9} className="absolute -bottom-0.5 -right-0.5 text-gray-900 bg-gray-900/60 rounded-full p-0.5" style={{ width: 10, height: 10 }} />
      </div>
    );
  };

  const TABS: { id: TabId; label: string; icon: typeof Globe }[] = [
    { id: "overview", label: "总览", icon: Globe },
    { id: "source", label: "来源溯源", icon: Radio },
    { id: "behavior", label: "行为追踪", icon: Activity },
    { id: "profile", label: "用户画像", icon: Fingerprint },
    { id: "intent", label: "意向打分", icon: Gauge },
    { id: "lead", label: "线索闭环", icon: Target },
    { id: "attribution", label: "归因分析", icon: GitMerge },
    { id: "reflux", label: "数据回流", icon: DatabaseBackup },
  ];

  const renderOverview = () => {
    const geo = data!.geoTotalVisitors || 0;
    const normal = Math.max(0, data!.totalVisitors - geo);
    const maxV = Math.max(1, data!.totalVisitors);
    // v6.39 近14天趋势：柱状 + 折线趋势（SVG 组合图）
    const trendDays = data!.days ?? [];
    const total14 = trendDays.reduce((s, d) => s + d.visits, 0);
    const avg14 = trendDays.length ? Math.round(total14 / trendDays.length) : 0;
    const peak = trendDays.reduce<DayStat | null>(
      (best, d) => (best == null || d.visits > best.visits ? d : best),
      null
    );
    const first14 = trendDays[0]?.visits ?? 0;
    const last14 = trendDays[trendDays.length - 1]?.visits ?? 0;
    const delta14 = first14 > 0 ? Math.round(((last14 - first14) / first14) * 100) : 0;
    const TW = 560; // SVG 视图宽
    const TH = 128; // SVG 视图高
    const padT = 8; // 顶部留白（放最高点/网格标签）
    const padB = 18; // 底部日期标签
    const plotH = TH - padT - padB;
    const maxDay = Math.max(1, ...trendDays.map((d) => d.visits));
    const slot = trendDays.length ? TW / trendDays.length : TW;
    const barW = Math.min(20, slot * 0.5);
    // 每个数据点坐标（x 居中于各自槽位）
    const pts = trendDays.map((d, i) => {
      const x = i * slot + slot / 2;
      const h = (d.visits / maxDay) * plotH;
      return { x, y: padT + plotH - h, h };
    });
    const linePath = pts.length ? `M ${pts.map((p) => `${p.x},${p.y}`).join(" L ")}` : "";
    const areaPath = pts.length
      ? `M ${pts[0].x},${padT + plotH} L ${pts.map((p) => `${p.x},${p.y}`).join(" L ")} L ${pts[pts.length - 1].x},${padT + plotH} Z`
      : "";
    const gridFrac = [0.25, 0.5, 0.75, 1];
    return (
      <div className="space-y-2">
        {/* 近 14 天趋势（柱 + 折线）——v6.40 移至总览最顶部，便于直接查看趋势 */}
        <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <TrendingUp size={10} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-300">近 14 天访问趋势</span>
            {trendDays.length > 0 && (
              <span className={`text-[9px] font-mono ml-auto ${delta14 >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {delta14 >= 0 ? "▲" : "▼"} {Math.abs(delta14)}%（较 14 天前）
              </span>
            )}
          </div>
          {trendDays.length > 0 && (
            <div className="flex items-center gap-3 mb-1.5 text-[9px] font-mono text-gray-500">
              <span>14 天总访问 <span className="text-gray-200">{total14}</span></span>
              <span>日均 <span className="text-gray-200">{avg14}</span></span>
              {peak && (
                <span>
                  峰值 <span className="text-amber-300">{peak.visits} 次</span>
                  <span className="text-gray-600">（{peak.date.slice(5)}）</span>
                </span>
              )}
            </div>
          )}
          {trendDays.length === 0 ? (
            <div className="text-[10px] font-mono text-gray-600 py-6 text-center">近 14 天暂无访问数据</div>
          ) : (
            <svg viewBox={`0 0 ${TW} ${TH}`} className="w-full max-w-[720px] mx-auto h-auto block" role="img" aria-label="近14天访问趋势">
              <defs>
                <linearGradient id="trendArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34D399" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#34D399" stopOpacity="0.02" />
                </linearGradient>
                <linearGradient id="trendBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.85" />
                  <stop offset="100%" stopColor="#22D3EE" stopOpacity="0.3" />
                </linearGradient>
              </defs>
              {/* 横向网格线 + 刻度 */}
              {gridFrac.map((f) => {
                const gy = padT + plotH - f * plotH;
                return (
                  <g key={f}>
                    <line x1="0" x2={TW} y1={gy} y2={gy} stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray="2 3" />
                    <text x={TW - 2} y={gy - 2} textAnchor="end" fontSize="7" fill="rgba(255,255,255,0.35)" fontFamily="monospace">
                      {Math.round(maxDay * f)}
                    </text>
                  </g>
                );
              })}
              {/* 柱状 */}
              {pts.map((p, i) => (
                <g key={i}>
                  <rect x={p.x - barW / 2} y={p.y} width={barW} height={Math.max(1, p.h)} rx={2} fill="url(#trendBar)">
                    <title>{`${trendDays[i].date} · ${trendDays[i].visits} 次`}</title>
                  </rect>
                  <text x={p.x} y={TH - 5} textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.5)" fontFamily="monospace">
                    {trendDays[i].date.slice(5)}
                  </text>
                </g>
              ))}
              {/* 趋势线下方渐变面积 */}
              <path d={areaPath} fill="url(#trendArea)" />
              {/* 趋势折线 */}
              <path d={linePath} fill="none" stroke="#34D399" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              {/* 趋势数据点 */}
              {pts.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="2.2" fill="#0B1120" stroke="#34D399" strokeWidth="1.2">
                  <title>{`${trendDays[i].date} · ${trendDays[i].visits} 次`}</title>
                </circle>
              ))}
            </svg>
          )}
        </div>
        {/* 普通 vs GEO 对比 */}
        <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Radio size={10} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-gray-300">普通流量 vs GEO 流量（访客数）</span>
          </div>
          <div className="flex items-end gap-2 h-20">
            <div className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex items-end justify-center" style={{ height: "100%" }}>
                <div className="w-full max-w-[60px] rounded-t bg-gradient-to-t from-cyan-500/40 to-cyan-400/80" style={{ height: `${(geo / maxV) * 100}%` }} />
              </div>
              <span className="text-[10px] font-mono text-cyan-300">{geo} GEO</span>
            </div>
            <div className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex items-end justify-center" style={{ height: "100%" }}>
                <div className="w-full max-w-[60px] rounded-t bg-gradient-to-t from-slate-500/40 to-slate-400/80" style={{ height: `${(normal / maxV) * 100}%` }} />
              </div>
              <span className="text-[10px] font-mono text-gray-400">{normal} 普通</span>
            </div>
          </div>
        </div>
        {/* 关键指标 */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            { label: "今日访问", value: data!.todayVisits, color: "#22D3EE" },
            { label: "累计访问", value: data!.totalVisits, color: "#A78BFA" },
            { label: "累计访客", value: data!.totalVisitors, color: "#FBBF24" },
            { label: "平均停留", value: fmtDur(data!.avgDuration), color: "#34D399" },
            { label: "跳出率", value: `${data!.bounceRate}%`, color: "#F472B6" },
            { label: "资料下载", value: data!.downloadCount, color: "#60A5FA" },
          ].map((k) => (
            <div key={k.label} className="px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02]">
              <div className="text-[16px] font-bold leading-none" style={{ color: k.color }}>{k.value}</div>
              <div className="text-[9px] font-mono text-gray-500 mt-1">{k.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSource = () => (
    <div className="space-y-2">
      {/* 分平台流量 */}
      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Radio size={10} className="text-violet-400" />
          <span className="text-[10px] font-mono text-gray-300">GEO 分平台流量（豆包 / Kimi / DeepSeek…）</span>
        </div>
        {(data!.geoPlatformBreakdown ?? []).length === 0 ? (
          <div className="text-[10px] font-mono text-gray-600 py-2">暂无 GEO 平台流量（需通过带 UTM 参数的 AI 来源链接访问）</div>
        ) : (
          <div className="space-y-1.5">
            {(data!.geoPlatformBreakdown ?? []).map((p) => (
              <div key={p.platform} className="flex items-center gap-2">
                <span className="text-[10px] font-mono w-16 text-gray-300">{p.platform}</span>
                <div className="flex-1 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(p.count / maxPlatform) * 100}%`, background: PLATFORM_COLOR[p.platform] || "#22D3EE" }} />
                </div>
                <span className="text-[10px] font-mono text-gray-500 w-8 text-right">{p.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* 引流问题词 TOP */}
      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <FileText size={10} className="text-amber-400" />
          <span className="text-[10px] font-mono text-gray-300">引流问题词 TOP（utm_term）</span>
        </div>
        {(data!.geoKeywordTop ?? []).length === 0 ? (
          <div className="text-[10px] font-mono text-gray-600 py-2">暂无问题词数据（AI 引擎跳转需带 utm_term 参数）</div>
        ) : (
          <div className="space-y-1.5">
            {(data!.geoKeywordTop ?? []).slice(0, 10).map((k, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[9px] font-mono text-gray-600 w-4">{i + 1}</span>
                <span className="text-[10px] font-mono text-gray-300 truncate flex-1">{k.keyword}</span>
                <div className="w-24 h-1 rounded-full bg-white/[0.05] overflow-hidden">
                  <div className="h-full bg-amber-400/70 rounded-full" style={{ width: `${(k.count / maxKeyword) * 100}%` }} />
                </div>
                <span className="text-[10px] font-mono text-gray-500 w-8 text-right">{k.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* 来源 TOP（referrer 域） */}
      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <ExternalLink size={10} className="text-sky-400" />
          <span className="text-[10px] font-mono text-gray-300">来源域 TOP</span>
        </div>
        {(data!.topReferrers ?? []).length === 0 ? (
          <div className="text-[10px] font-mono text-gray-600 py-2">暂无来源（直接访问占多数）</div>
        ) : (
          <div className="space-y-1">
            {(data!.topReferrers ?? []).slice(0, 6).map((r) => (
              <div key={r.referrer} className="flex items-center justify-between text-[10px] font-mono">
                <span className="text-gray-300 truncate max-w-[70%]">{r.referrer}</span>
                <span className="text-gray-500 flex-shrink-0">{r.count} 次</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderBehavior = () => (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: "平均停留时长", value: fmtDur(data!.avgDuration), color: "#34D399" },
          { label: "跳出率", value: `${data!.bounceRate}%`, color: "#F472B6" },
          { label: "资料下载次数", value: data!.downloadCount, color: "#60A5FA" },
          { label: "工具使用次数", value: data!.toolUseCount, color: "#A78BFA" },
        ].map((k) => (
          <div key={k.label} className="px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02]">
            <div className="text-[16px] font-bold leading-none" style={{ color: k.color }}>{k.value}</div>
            <div className="text-[9px] font-mono text-gray-500 mt-1">{k.label}</div>
          </div>
        ))}
      </div>
      {/* 页面 TOP */}
      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <FileText size={10} className="text-violet-400" />
          <span className="text-[10px] font-mono text-gray-300">页面访问量 TOP（含官博流量浏览次数）</span>
        </div>
        <div className="space-y-1">
          {(data!.topPages ?? []).slice(0, 10).map((p) => (
            <div key={p.page} className="flex items-center gap-2 text-[10px] font-mono">
              <span className="text-gray-300 truncate flex-1">{(p.page || "/").replace(/^\//, "") || "/"}</span>
              <span className="text-gray-500 flex-shrink-0">{p.count} 次</span>
            </div>
          ))}
        </div>
      </div>
      {/* 设备分类 */}
      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Smartphone size={10} className="text-pink-400" />
          <span className="text-[10px] font-mono text-gray-300">设备分类</span>
        </div>
        {(data!.deviceBreakdown ?? []).length === 0 ? (
          <div className="text-[10px] font-mono text-gray-600 py-2">暂无设备数据</div>
        ) : (
          <div className="space-y-1.5">
            {(data!.deviceBreakdown ?? []).map((d) => {
              const meta = DEVICE_META[d.device] || DEVICE_META.未知;
              const Icon = meta.icon;
              const maxDev = Math.max(1, ...(data!.deviceBreakdown ?? []).map((x) => x.count));
              return (
                <div key={d.device} className="flex items-center gap-2">
                  <Icon size={11} style={{ color: meta.color }} />
                  <span className="text-[10px] font-mono text-gray-300 w-8">{meta.label}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(d.count / maxDev) * 100}%`, background: meta.color }} />
                  </div>
                  <span className="text-[10px] font-mono text-gray-500 w-8 text-right">{d.count}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderProfile = () => (
    <div className="space-y-2">
      {/* 新/回访 + 设备 */}
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
          <div className="text-[10px] font-mono text-gray-300 mb-2">新访客 / 回访访客</div>
          <div className="flex items-center justify-center gap-4 h-14">
            <div className="text-center">
              <div className="text-[18px] font-bold text-cyan-300 leading-none">{data!.newVisitors}</div>
              <div className="text-[9px] font-mono text-gray-500 mt-1">新访客</div>
            </div>
            <div className="text-[12px] text-gray-600">|</div>
            <div className="text-center">
              <div className="text-[18px] font-bold text-emerald-300 leading-none">{data!.returningVisitors}</div>
              <div className="text-[9px] font-mono text-gray-500 mt-1">回访</div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
          <div className="text-[10px] font-mono text-gray-300 mb-2">今日地区 TOP</div>
          <div className="text-[10px] font-mono text-gray-400 leading-relaxed">
            {(data!.topTodayRegions ?? []).length === 0 ? "暂无" : (data!.topTodayRegions ?? []).map((r) => `${r.region} ${r.count}`).join(" · ")}
          </div>
        </div>
      </div>
      {/* 地区 TOP */}
      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <MapPin size={10} className="text-rose-400" />
          <span className="text-[10px] font-mono text-gray-300">访客地区 TOP（IP 归属）</span>
        </div>
        {(data!.topRegions ?? []).length === 0 ? (
          <div className="text-[10px] font-mono text-gray-600 py-2">暂无地区数据</div>
        ) : (
          <div className="space-y-1.5">
            {(data!.topRegions ?? []).slice(0, 8).map((r) => (
              <div key={r.region}>
                <div className="flex items-center justify-between text-[10px] font-mono">
                  <span className="text-gray-300 truncate">{r.region}</span>
                  <span className="text-gray-500">{r.count} IP</span>
                </div>
                <div className="mt-0.5 h-1 rounded-full bg-white/[0.05] overflow-hidden">
                  <div className="h-full bg-rose-400/70 rounded-full" style={{ width: `${(r.count / maxRegion) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* 访问时段 */}
      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Clock size={10} className="text-amber-400" />
          <span className="text-[10px] font-mono text-gray-300">访问时段分布（0-23 时）</span>
        </div>
        <div className="flex items-end gap-[2px] h-16">
          {(data!.hourBreakdown ?? []).map((h) => (
            <div key={h.hour} className="flex-1 flex flex-col items-center gap-0.5">
              <div className="w-full flex items-end justify-center" style={{ height: "100%" }}>
                <div className="w-full max-w-[10px] rounded-sm bg-gradient-to-t from-amber-500/30 to-amber-400/70" style={{ height: `${Math.max(3, (h.count / maxHour) * 100)}%` }} title={`${h.hour} 时 · ${h.count} 次`} />
              </div>
              {h.hour % 6 === 0 && <span className="text-[7px] font-mono text-gray-600">{h.hour}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderIntent = () => {
    const scored = (data!.visitors ?? []).filter((v) => v.intentScore !== undefined).sort((a, b) => (b.intentScore || 0) - (a.intentScore || 0));
    return (
      <div className="space-y-2">
        <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Gauge size={10} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-300">访客意向分（行为自动打分 → 可直接生成匿名线索）</span>
          </div>
          <div className="text-[9px] font-mono text-gray-600 mb-2">计分规则：停留时长(≤30s) + 页面深度(≤30) + 资料下载(≤30) + 工具使用(≤20)</div>
          {scored.length === 0 ? (
            <div className="text-[10px] font-mono text-gray-600 py-2">暂无打分数据（需新版埋点采集行为后产生）</div>
          ) : (
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {scored.slice(0, 15).map((v, i) => {
                const lv = intentLevel(v.intentScore || 0);
                return (
                  <div key={i} className="flex items-center gap-2 px-2 py-1 rounded border border-white/[0.04] bg-white/[0.01]">
                    <Avatar v={v} size={18} />
                    <span className="text-[10px] font-mono text-gray-300 truncate flex-1">{visitorLabel(v)}</span>
                    <span className="text-[9px] font-mono text-gray-600">{v.visits}次 · {v.pageCount}页</span>
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ color: lv.color, background: `${lv.color}1a` }}>{lv.label}</span>
                    <span className="text-[12px] font-bold" style={{ color: lv.color }}>{v.intentScore}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {/* 匿名线索（高意向 → 一键生成 DSO 匿名线索） */}
        <div className="rounded-lg border border-cyan-500/15 bg-cyan-500/[0.03] p-3">
          <div className="text-[10px] font-mono text-cyan-300 mb-1.5">DSO 匿名线索生成</div>
          <div className="text-[9px] font-mono text-gray-500 mb-2">高意向访客（≥60 分）可沉淀为匿名线索，后续留资自动关联补全为实名线索。</div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono text-gray-300">高意向访客：</span>
            <span className="text-[14px] font-bold text-emerald-400">{scored.filter((v) => (v.intentScore || 0) >= 60).length}</span>
            <span className="text-[10px] font-mono text-gray-300">人</span>
            <span className="text-[10px] font-mono text-gray-500 ml-auto">共 {scored.length} 位已打分</span>
          </div>
        </div>
      </div>
    );
  };

  const renderLead = () => (
    <div className="space-y-2">
      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Target size={10} className="text-emerald-400" />
          <span className="text-[10px] font-mono text-gray-300">线索转化链路：GEO 访客 → 留资线索 → 成交</span>
        </div>
        <div className="text-[9px] font-mono text-gray-600 mb-2">官网留资入口：右下角「免费咨询/预约演示」浮窗，提交后进入 CRM 线索池。</div>
        <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
          <span className="px-2 py-1 rounded border border-white/[0.06] bg-white/[0.02]"><b className="text-cyan-300">{data!.geoTotalVisitors}</b> GEO 访客</span>
          <span className="text-gray-600">→</span>
          <span className="px-2 py-1 rounded border border-white/[0.06] bg-white/[0.02]"><b className="text-violet-300">{scoredIntents(data!)}</b> 高意向匿名线索</span>
          <span className="text-gray-600">→</span>
          <span className="px-2 py-1 rounded border border-white/[0.06] bg-white/[0.02]"><b className="text-emerald-300">{data!.downloadCount}</b> 资料下载</span>
        </div>
      </div>
      {/* 最近访问（可识别潜在线索） */}
      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Clock size={10} className="text-amber-400" />
          <span className="text-[10px] font-mono text-gray-300">最近访问（高潜访客线索追踪）</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px] font-mono">
            <thead>
              <tr className="text-gray-600 text-left">
                <th className="pr-2 pb-1 font-normal">时间</th>
                <th className="pr-2 pb-1 font-normal">地区</th>
                <th className="pb-1 font-normal">页面</th>
              </tr>
            </thead>
            <tbody>
              {(data!.recent ?? []).slice(0, 10).map((v, i) => (
                <tr key={i} className="text-gray-400 border-t border-white/[0.04]">
                  <td className="py-1 pr-2 whitespace-nowrap">{fmtTime(v.ts)}</td>
                  <td className="py-1 pr-2 text-gray-500">{v.region || "—"}</td>
                  <td className="py-1 truncate max-w-[140px]">{v.title || v.page || "/"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderAttribution = () => {
    const platforms = data!.geoPlatformBreakdown ?? [];
    const keywords = data!.geoKeywordTop ?? [];
    return (
      <div className="space-y-2">
        <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <GitMerge size={10} className="text-violet-400" />
            <span className="text-[10px] font-mono text-gray-300">归因分析：哪些 GEO 素材带来流量</span>
          </div>
          <div className="text-[9px] font-mono text-gray-600 mb-2">按引流平台 + 问题词反向追踪：AI 引用哪条内容 → 带来多少访客。</div>
          {platforms.length === 0 && keywords.length === 0 ? (
            <div className="text-[10px] font-mono text-gray-600 py-2">暂无归因数据（需带 UTM 参数的 AI 来源流量）</div>
          ) : (
            <div className="space-y-3">
              {platforms.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono text-gray-400 mb-1">引流平台贡献</div>
                  <div className="flex items-end gap-2 h-16">
                    {platforms.slice(0, 8).map((p) => (
                      <div key={p.platform} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full flex items-end justify-center" style={{ height: "100%" }}>
                          <div className="w-full max-w-[26px] rounded-t" style={{ height: `${(p.count / maxPlatform) * 100}%`, background: PLATFORM_COLOR[p.platform] || "#22D3EE" }} title={p.platform} />
                        </div>
                        <span className="text-[8px] font-mono text-gray-500 truncate w-full text-center">{p.platform}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {keywords.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono text-gray-400 mb-1">引流问题词贡献</div>
                  <div className="space-y-1">
                    {keywords.slice(0, 8).map((k, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-[10px] font-mono text-gray-300 truncate flex-1">{k.keyword}</span>
                        <div className="w-20 h-1 rounded-full bg-white/[0.05] overflow-hidden">
                          <div className="h-full bg-violet-400/70 rounded-full" style={{ width: `${(k.count / maxKeyword) * 100}%` }} />
                        </div>
                        <span className="text-[10px] font-mono text-gray-500 w-8 text-right">{k.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderReflux = () => (
    <div className="space-y-2">
      <div className="rounded-lg border border-cyan-500/15 bg-cyan-500/[0.03] p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <DatabaseBackup size={10} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-300">数据回流 → 反哺上游 GEO 知识库</span>
        </div>
        <div className="text-[9px] font-mono text-gray-500 mb-2">下游访客行为反过来指导上游生产内容：自动输出任务给 GEO 模块，围绕【私有化部署 / 白标代理】等补齐问答素材到 AI 素材库。</div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => runReflux("analyze")}
            disabled={refluxBusy}
            className="px-3 py-1.5 rounded border border-cyan-400/40 text-[11px] font-mono text-cyan-300 hover:bg-cyan-400/10 transition-colors disabled:opacity-50"
          >
            {refluxBusy ? "分析中…" : "① 分析素材缺口"}
          </button>
          <button
            onClick={() => runReflux("generate")}
            disabled={refluxBusy}
            className="px-3 py-1.5 rounded border border-emerald-400/40 text-[11px] font-mono text-emerald-300 hover:bg-emerald-400/10 transition-colors disabled:opacity-50"
          >
            {refluxBusy ? "生成中…" : "② 生成素材建议（写入知识库）"}
          </button>
        </div>
        {refluxMsg && (
          <div className={`mt-2 text-[10px] font-mono ${refluxMsg.ok ? "text-emerald-300" : "text-rose-300"}`}>{refluxMsg.text}</div>
        )}
      </div>
      {/* 素材缺口命中 */}
      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <FileText size={10} className="text-amber-400" />
          <span className="text-[10px] font-mono text-gray-300">访客问题词 × 待补充素材命中</span>
        </div>
        {topicHits.length === 0 ? (
          <div className="text-[10px] font-mono text-gray-600 py-2">点击「分析素材缺口」查看访客搜索意图与素材覆盖情况</div>
        ) : (
          <div className="space-y-1.5">
            {topicHits.map((t) => (
              <div key={t.topic} className="flex items-center justify-between text-[10px] font-mono">
                <span className="text-gray-300">{t.topic}</span>
                <span className="text-gray-500">{t.count} 次</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* 预设回流素材包（私有化部署 / 白标代理） */}
      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
        <div className="text-[10px] font-mono text-gray-300 mb-2">本轮回流素材包（提交 AI 素材库）</div>
        <div className="space-y-1 text-[10px] font-mono text-gray-400">
          <div>· 私有化部署：私有化 / 本地部署 / 内网部署 / 自主可控 问答</div>
          <div>· 白标代理：白标 / 白牌 / 贴牌 / OEM / 渠道代理 问答</div>
          <div>· 数据安全：数据不出域 / 合规 / 等保 问答</div>
          <div>· 企业AI部署：接入 / 集成 / 实施 实操问答</div>
        </div>
      </div>
    </div>
  );

  const scoredIntents = (d: StatsData) => (d.visitors ?? []).filter((v) => (v.intentScore || 0) >= 60).length;

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] flex flex-col flex-shrink-0">
      {/* 头部 */}
      <div className="px-3 py-2 border-b border-white/[0.06] flex items-center gap-2 flex-shrink-0">
        <Globe size={11} className="text-cyan-400" />
        <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
          GEO 流量分析中心
        </span>
        <span className="text-[9px] font-mono text-gray-600">www.xshetech.com</span>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="ml-auto flex items-center gap-1 px-2 py-1 rounded border border-white/[0.08] text-[10px] font-mono text-gray-400 hover:text-white hover:border-white/20 transition-colors"
        >
          <RefreshCw size={10} className={refreshing ? "animate-spin" : ""} />
          刷新
        </button>
      </div>

      {/* Tab 导航 */}
      <div className="flex items-center gap-0.5 px-2 pt-2 flex-wrap flex-shrink-0">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono transition-all ${active ? "text-white bg-white/[0.05]" : "text-gray-500 hover:text-gray-300"}`}
              style={active ? { color: "#22D3EE" } : {}}
            >
              <Icon size={9} />
              {t.label}
            </button>
          );
        })}
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-10 text-gray-500 gap-2">
          <Loader2 size={14} className="animate-spin" />
          <span className="text-[11px] font-mono">正在拉取 GEO 流量数据…</span>
        </div>
      ) : error ? (
        <div className="px-3 py-6 text-center text-[11px] font-mono text-amber-400/90">{error}</div>
      ) : data ? (
        <div className="p-3">
          {tab === "overview" && renderOverview()}
          {tab === "source" && renderSource()}
          {tab === "behavior" && renderBehavior()}
          {tab === "profile" && renderProfile()}
          {tab === "intent" && renderIntent()}
          {tab === "lead" && renderLead()}
          {tab === "attribution" && renderAttribution()}
          {tab === "reflux" && renderReflux()}
        </div>
      ) : null}
    </div>
  );
}
